import React, { useEffect, useState, useCallback } from "react";
import { Box, CircularProgress, Backdrop, Snackbar, Alert } from "@mui/material";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import {
    format,
    startOfWeek,
    endOfWeek,
    startOfDay,
    endOfDay,
    startOfMonth,
    endOfMonth
} from "date-fns";

// Atoms
import {
    calendarViewAtom,
    calendarDateAtom,
    calendarSelectedProvidersAtom,
    calendarCellDurationAtom,
    calendarShowSaturdayAtom,
    calendarShowSundayAtom
} from "../../atoms/calendar";
import {
    showBookingDetailsAtom,
    setBookingDateTimeAtom,
    setBookingProviderIdAtom,
    setDetailAtom,
    providersListAtom
} from "../../atoms/booking";
import {
    openBlockedTimeDialogAtom,
    selectedBuIdAtom,
    recordIdAtom
} from "../../atoms/blockedTime";
import {
    mbxUserAtom,
    companyMasterTz,
    isMbxUserProvider,
    isMbxUserVerifiedStaff
} from "../../atoms/atoms";

// Hooks
import { useCalendarData } from "../../hooks/useCalendarData";

// Components
import CalendarToolbar from "./CalendarToolbar";
import CalendarGrid from "./CalendarGrid";
import CalendarMonthGrid from "./CalendarMonthGrid";
import BlockedTimeModal from "../../user/components/BlockedTimeModal";

// Services
import { getUserFromCache } from "../../user/UserCommon";
import { execReadBySortkey } from "../../modules/DBService";
import { getCurrentTimeAdjustedToNextHour } from "../../utils/SchedulerFunctions/DateNavigatorFormatter";

/**
 * CalendarPage - Main calendar page component.
 * Replaces ProviderScheduleDevEx for desktop staff users.
 */
export default function CalendarPage() {
    // --- Atoms ---
    const [view, setView] = useAtom(calendarViewAtom);
    const [currentDate, setCurrentDate] = useAtom(calendarDateAtom);
    const [selectedProviders, setSelectedProviders] = useAtom(
        calendarSelectedProvidersAtom
    );
    const [cellDuration, setCellDuration] = useAtom(calendarCellDurationAtom);
    const [showSaturday, setShowSaturday] = useAtom(calendarShowSaturdayAtom);
    const [showSunday, setShowSunday] = useAtom(calendarShowSundayAtom);
    const [showBookingDetails, setShowBookingDetails] = useAtom(showBookingDetailsAtom);
    const [, setBookingDateTime] = useAtom(setBookingDateTimeAtom);
    const [, setBookingProviderId] = useAtom(setBookingProviderIdAtom);
    const [, setDetail] = useAtom(setDetailAtom);
    const [, setProviderListForBookingDialog] = useAtom(providersListAtom);
    const [openBlockedTimeDialog, setOpenBlockedTimeDialog] = useAtom(
        openBlockedTimeDialogAtom
    );
    const [, setSelectedBuId] = useAtom(selectedBuIdAtom);
    const [, setRecordId] = useAtom(recordIdAtom);
    const [mbxUser] = useAtom(mbxUserAtom);
    const [masterTz] = useAtom(companyMasterTz);
    const [isProviderUser] = useAtom(isMbxUserProvider);
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);

    // --- Local state ---
    const [allProviders, setAllProviders] = useState([]);
    const [providersLoading, setProvidersLoading] = useState(false);
    const [clickedCellProps, setClickedCellProps] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    let [searchParams, setSearchParams] = useSearchParams();

    // --- Compute date range ---
    const getDateRange = useCallback(() => {
        if (view === "Day") {
            return {
                startDate: format(
                    startOfDay(currentDate),
                    "yyyy-MM-dd'T'00:00:00"
                ),
                endDate: format(
                    endOfDay(currentDate),
                    "yyyy-MM-dd'T'23:59:59"
                )
            };
        }
        if (view === "Month") {
            const monthStart = startOfMonth(currentDate);
            const monthEnd = endOfMonth(currentDate);
            return {
                startDate: format(monthStart, "yyyy-MM-dd'T'00:00:00"),
                endDate: format(monthEnd, "yyyy-MM-dd'T'23:59:59")
            };
        }
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
        return {
            startDate: format(weekStart, "yyyy-MM-dd'T'00:00:00"),
            endDate: format(weekEnd, "yyyy-MM-dd'T'23:59:59")
        };
    }, [currentDate, view]);

    const { startDate, endDate } = getDateRange();
    const providerIds = selectedProviders.map((p) => p.id);
    const companyId = mbxUser?.companyId || mbxUser?.company?.id || "";

    // --- Fetch calendar data ---
    const {
        data: calendarData,
        isLoading: calendarLoading,
        refetch
    } = useCalendarData(companyId, providerIds, startDate, endDate);

    // --- Fetch provider list on mount (or auto-select for provider users) ---
    useEffect(() => {
        if (isProviderUser && mbxUser?.provider) {
            // Provider users only see their own calendar
            const p = mbxUser.provider;
            const providerOption = {
                id: p.id,
                firstname: p.firstname,
                lastname: p.lastname,
                emailaddress: p.emailaddress,
                timezone: p.timezone,
                name: `${p.firstname} ${p.lastname}`
            };
            setSelectedProviders([providerOption]);
            setAllProviders([providerOption]);
        } else {
            const loggedInUser = getUserFromCache();
            if (loggedInUser?.companyId) {
                fetchProviders(loggedInUser.companyId);
            }
        }
    }, [isProviderUser]);

    // --- Auto-select provider from URL param ---
    useEffect(() => {
        const pIds = searchParams.get("pIds");
        if (pIds && allProviders.length > 0 && selectedProviders.length === 0) {
            const ids = pIds.split(",");
            const matched = allProviders.filter((p) => ids.includes(p.id));
            if (matched.length) {
                setSelectedProviders(matched);
            }
        }
    }, [allProviders, searchParams]);

    // --- Refetch calendar when BookingDialog closes (booking created/updated) ---
    const prevShowBookingDetails = React.useRef(showBookingDetails);
    useEffect(() => {
        if (prevShowBookingDetails.current && !showBookingDetails) {
            // Dialog just closed — refetch after a short delay for DB consistency
            setTimeout(() => refetch(), 1000);
        }
        prevShowBookingDetails.current = showBookingDetails;
    }, [showBookingDetails, refetch]);

    // --- Update URL when providers change ---
    useEffect(() => {
        if (selectedProviders.length > 0) {
            searchParams.set(
                "pIds",
                selectedProviders.map((p) => p.id).join(",")
            );
            setSearchParams(searchParams);
        }
    }, [selectedProviders]);

    const fetchProviders = async (companyId) => {
        setProvidersLoading(true);
        try {
            let providersList = await execReadBySortkey({
                opname: "providerByCompany",
                op: /* GraphQL */ `
                    query ProviderByCompany(
                        $companyId: String
                        $firstnameLastnameCreatedAt: ModelProviderByCompanyCompositeKeyConditionInput
                        $sortDirection: ModelSortDirection
                        $filter: ModelProviderFilterInput
                        $limit: Int
                        $nextToken: String
                    ) {
                        providerByCompany(
                            companyId: $companyId
                            firstnameLastnameCreatedAt: $firstnameLastnameCreatedAt
                            sortDirection: $sortDirection
                            filter: $filter
                            limit: $limit
                            nextToken: $nextToken
                        ) {
                            items {
                                id
                                firstname
                                lastname
                                emailaddress
                                phone
                                timezone
                                user {
                                    id
                                }
                            }
                            nextToken
                        }
                    }
                `,
                id: { companyId },
                filter: {
                    and: [
                        { active: { ne: false } },
                        { deleted: { ne: true } }
                    ]
                }
            });

            if (providersList?.items?.length) {
                const items = providersList.items.map((item) => ({
                    ...item,
                    name: `${item.firstname} ${item.lastname}`,
                    email: item.emailaddress
                }));
                setAllProviders(items);
                setProviderListForBookingDialog(items);
            }
        } catch (err) {
            console.error("Failed to fetch providers:", err);
        } finally {
            setProvidersLoading(false);
        }
    };

    // --- Cell click: open BookingDialog (same as Dispatcher Co-pilot) ---
    const handleCellClick = useCallback(
        (providerId, dateStr, timeKey) => {
            if (!isVerifiedStaff) return;
            const provider = selectedProviders.find(
                (p) => p.id === providerId
            );
            if (!provider) return;

            const cellDate = new Date(`${dateStr}T${timeKey}:00`);

            setDetail({ selectedLocationId: "customerLocation" });
            setBookingProviderId(providerId);
            setBookingDateTime(cellDate);
            setShowBookingDetails(true);
        },
        [
            selectedProviders,
            setDetail,
            setBookingProviderId,
            setBookingDateTime,
            setShowBookingDetails
        ]
    );

    // --- Add booking button click (same pattern as nav menu) ---
    const handleAddBookingClick = useCallback(() => {
        if (!isVerifiedStaff) return;
        setDetail({ selectedLocationId: "customerLocation" });
        setBookingDateTime(getCurrentTimeAdjustedToNextHour());
        if (selectedProviders.length > 0) {
            setBookingProviderId(selectedProviders[0].id);
        }
        setShowBookingDetails(true);
    }, [
        selectedProviders,
        setDetail,
        setBookingProviderId,
        setBookingDateTime,
        setShowBookingDetails
    ]);

    // --- Event edit handler ---
    const handleEventEdit = useCallback(
        (event, type) => {
            if (type === "blocked") {
                // Open blocked time modal
                const provider = selectedProviders.find(
                    (p) => p.id === event.id?.split("|")[0]?.replace("C-", "")
                ) || selectedProviders[0];

                if (event.buId) {
                    setSelectedBuId(event.buId);
                }
                if (provider && companyId) {
                    setRecordId(`C-${companyId}|P-${provider.id}`);
                }
                setOpenBlockedTimeDialog(true);
            } else {
                // For bookings, open the manage booking dialog
                // Set atoms that the dialog reads
                setShowBookingDetails(true);
            }
        },
        [
            selectedProviders,
            companyId,
            setSelectedBuId,
            setRecordId,
            setOpenBlockedTimeDialog,
            setShowBookingDetails
        ]
    );

    // --- Blocked time delete handler ---
    const handleEventDelete = useCallback(
        (event, type) => {
            if (type === "blocked" && event.buId) {
                setSelectedBuId(event.buId);
                const provider = selectedProviders[0];
                if (provider && companyId) {
                    setRecordId(`C-${companyId}|P-${provider.id}`);
                }
                setOpenBlockedTimeDialog(true);
            }
        },
        [selectedProviders, companyId, setSelectedBuId, setRecordId, setOpenBlockedTimeDialog]
    );

    const showSnackMsg = useCallback((message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 64px)",
                backgroundColor: "#fff",
                position: "relative",
                zIndex: 0
            }}
        >
            {/* Toolbar */}
            <CalendarToolbar
                providers={allProviders}
                selectedProviders={selectedProviders}
                onProviderChange={setSelectedProviders}
                currentDate={currentDate}
                onDateChange={setCurrentDate}
                view={view}
                onViewChange={setView}
                cellDuration={cellDuration}
                onCellDurationChange={setCellDuration}
                showSaturday={showSaturday}
                onShowSaturdayChange={setShowSaturday}
                showSunday={showSunday}
                onShowSundayChange={setShowSunday}
                onAddBooking={handleAddBookingClick}
                isLoading={providersLoading}
                hideProviderSelector={isProviderUser}
                canAddBooking={isVerifiedStaff}
            />

            {/* Calendar grid */}
            {selectedProviders.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        color: "text.secondary"
                    }}
                >
                    Select a provider to view their calendar
                </Box>
            ) : view === "Month" ? (
                <CalendarMonthGrid
                    calendarData={calendarData}
                    providers={selectedProviders}
                    anchorDate={currentDate}
                    onDayClick={(day) => {
                        setCurrentDate(day);
                        setView("Day");
                    }}
                />
            ) : (
                <CalendarGrid
                    calendarData={calendarData}
                    providers={selectedProviders}
                    anchorDate={currentDate}
                    view={view}
                    cellDuration={cellDuration}
                    showSaturday={showSaturday}
                    showSunday={showSunday}
                    onCellClick={handleCellClick}
                    onEventEdit={handleEventEdit}
                    onEventDelete={handleEventDelete}
                    canAddBooking={isVerifiedStaff}
                />
            )}

            {/* Loading overlay - only when providers selected and first load */}
            <Backdrop
                open={
                    calendarLoading &&
                    !calendarData &&
                    selectedProviders.length > 0
                }
                sx={{ color: "#fff", zIndex: 20 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Blocked Time Modal */}
            {openBlockedTimeDialog && (
                <BlockedTimeModal
                    open={openBlockedTimeDialog}
                    onClose={() => setOpenBlockedTimeDialog(false)}
                    startDateInitial={clickedCellProps?.startDate}
                    endDateInitial={clickedCellProps?.startDate}
                    handleSave={async () => {
                        refetch();
                    }}
                />
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() =>
                        setSnackbar((s) => ({ ...s, open: false }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
